import React, { useState, useEffect, useContext } from 'react';
import { ClipboardDocumentListIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
  PageHeader,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Badge,
  StatusBadge,
  EmptyState,
  ErrorAlert,
  Skeleton,
  SuccessAlert,
  WarningAlert,
} from '../components/ui';
import { formatCurrency, formatDate, formatDateTime } from '../lib/format';

interface Order {
  id: string;
  type: 'vps' | 'email' | 'database' | 'domain' | 'messaging' | 'emailing' | 'whatsapp';
  credits?: number;
  domain_name?: string;
  renewal_ht?: number;
  renewal_ttc?: number;
  offer_name?: string;
  offer_code?: string;
  price_ht: number;
  price_ttc: number;
  status: string;
  created_at: string;
  vcores?: number;
  ram_gb?: number;
  ssd_gb?: number;
  location?: string;
  os_mode?: string;
  os_family?: string;
  os_version?: string;
  os_app?: string;
  premium_backup?: boolean;
  snapshot_backup?: boolean;
  extra_storage_units?: number;
  local_part?: string;
  domain?: string;
  engine?: string;
  storage_gb?: number;
}

const LOCATION_LABEL: Record<string, string> = {
  fr: 'France',
  de: 'Germany',
  nl: 'Netherlands',
  us: 'United States',
};

function getOSLabel(order: Order) {
  if (order.os_mode === 'os-app' && order.os_app)
    return { 'debian-docker': 'Docker (Debian 12)', 'debian-n8n': 'n8n (Debian 12)' }[order.os_app] || order.os_app;
  if (order.os_family && order.os_version) return `${order.os_family} ${order.os_version}`;
  return 'Not specified';
}

export default function Orders() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Order | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${keycloak?.token}` };
      const [vpsRes, emailRes, dbRes, domainRes, msgRes, emlRes, waRes] = await Promise.all([
        fetch(`${API_URL}/orders/vps`, { headers }),
        fetch(`${API_URL}/orders/email`, { headers }),
        fetch(`${API_URL}/orders/database`, { headers }),
        fetch(`${API_URL}/orders/domain`, { headers }),
        fetch(`${API_URL}/orders/messaging`, { headers }),
        fetch(`${API_URL}/orders/emailing`, { headers }),
        fetch(`${API_URL}/orders/whatsapp`, { headers }),
      ]);
      if (!vpsRes.ok) throw new Error('Failed to fetch VPS orders');
      if (!emailRes.ok) throw new Error('Failed to fetch email orders');
      if (!dbRes.ok) throw new Error('Failed to fetch database orders');
      if (!domainRes.ok) throw new Error('Failed to fetch domain orders');
      if (!msgRes.ok) throw new Error('Failed to fetch messaging orders');
      if (!emlRes.ok) throw new Error('Failed to fetch emailing orders');
      if (!waRes.ok) throw new Error('Failed to fetch whatsapp orders');
      const [vps, email, db, domain, msg, eml, wa] = await Promise.all([
        vpsRes.json(),
        emailRes.json(),
        dbRes.json(),
        domainRes.json(),
        msgRes.json(),
        emlRes.json(),
        waRes.json(),
      ]);
      const combined: Order[] = [
        ...(vps || []).map((o: any) => ({ ...o, type: o.type || 'vps' })),
        ...(email || []).map((o: any) => ({ ...o, type: o.type || 'email' })),
        ...(db || []).map((o: any) => ({ ...o, type: o.type || 'database' })),
        ...(domain || []).map((o: any) => ({ ...o, type: o.type || 'domain' })),
        ...(msg || []).map((o: any) => ({ ...o, type: 'messaging' })),
        ...(eml || []).map((o: any) => ({ ...o, type: 'emailing' })),
        ...(wa || []).map((o: any) => ({ ...o, type: 'whatsapp' })),
      ];
      combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setOrders(combined);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: 'Cancel order',
      message: `Are you sure you want to cancel order #${selected.id}? This action cannot be undone.`,
      confirmLabel: 'Cancel order',
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/orders/${selected.type}/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to cancel order');
      }
      setSelected(null);
      await fetchOrders();
      toast('Order cancelled successfully!', 'success');
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const typeLabel = (t: Order['type']) =>
    ({ vps: 'VPS', email: 'Email', database: 'Database', domain: 'Domain', messaging: 'Messaging', emailing: 'Emailing', whatsapp: 'WhatsApp' }[t]);

  const orderOfferLabel = (o: Order) => {
    if (o.type === 'domain') return o.domain_name || '—';
    if (o.type === 'messaging') return `${o.credits} message credits`;
    if (o.type === 'emailing') return `${o.credits} email credits`;
    if (o.type === 'whatsapp') return `${o.credits} WhatsApp credits`;
    return o.offer_name || o.offer_code || '—';
  };

  const pendingMessage = (type: Order['type']) =>
    ({
      database: 'Your database order is pending. An administrator will provision it shortly.',
      email: 'Your email order is pending. An administrator will review it shortly.',
      domain: 'Your domain order is pending. An administrator will register it shortly.',
      messaging: 'Your messages credits order is pending. An administrator will credit your account shortly.',
      emailing: 'Your email credits order is pending. An administrator will credit your account shortly.',
      whatsapp: 'Your WhatsApp credits order is pending. An administrator will credit your account shortly.',
      vps: 'Your order is pending. An administrator will review and provision your VPS shortly.',
    }[type]);

  const validatedMessage = (o: Order) =>
    ({
      database: 'Your database order has been validated! Your database is now available in the Databases section.',
      email: 'Your email order has been validated!',
      domain: `Your domain order has been validated! ${o.domain_name || ''} will be available in the Domains section shortly.`,
      messaging: `Your messages credits order has been validated! ${o.credits} credits have been added to your messaging token.`,
      emailing: `Your email credits order has been validated! ${o.credits} credits have been added to your emailing token.`,
      whatsapp: `Your WhatsApp credits order has been validated! ${o.credits} credits have been added to your WhatsApp token.`,
      vps: 'Your order has been validated! Your VPS is now available in the VPS section.',
    }[o.type]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShoppingCartIcon />}
        title="Orders"
        description="View your order history and status."
      />

      {error && <ErrorAlert message={error} />}

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={<ClipboardDocumentListIcon />}
          title="No orders yet"
          description="Your order history will appear here once you place your first order."
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Offer</TableHead>
                <TableHead className="text-right hidden md:table-cell">Price (excl. VAT)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Date</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={`${o.type}-${o.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">#{o.id}</TableCell>
                  <TableCell>
                    <Badge variant="muted">{typeLabel(o.type)}</Badge>
                  </TableCell>
                  <TableCell className="font-medium text-foreground">{orderOfferLabel(o)}</TableCell>
                  <TableCell className="text-right hidden md:table-cell tabular-nums">
                    {formatCurrency(o.price_ht)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={o.status} />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                    {formatDate(o.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelected(o)}>
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!selected} onClose={() => !deleting && setSelected(null)} size="2xl">
        {selected && (
          <>
            <DialogHeader
              title={`Order #${selected.id}`}
              description={
                <span>
                  Status: <StatusBadge status={selected.status} />
                </span>
              }
              onClose={() => !deleting && setSelected(null)}
            />
            <DialogBody className="space-y-5">
              <div className="rounded-lg bg-muted/40 border border-border p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  {typeLabel(selected.type)} order
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {selected.type === 'domain' && (
                    <>
                      <Field label="Domain name" value={selected.domain_name} />
                      <Field label="First year (excl. VAT)" value={formatCurrency(selected.price_ht)} />
                      <Field label="Renewal / yr (excl. VAT)" value={formatCurrency(selected.renewal_ht || 0)} />
                    </>
                  )}
                  {selected.type === 'database' && (
                    <>
                      <Field label="Offer" value={selected.offer_name || selected.offer_code} />
                      <Field
                        label="Engine"
                        value={
                          selected.engine === 'mongodb'
                            ? 'MongoDB'
                            : selected.engine === 'mysql'
                            ? 'MySQL'
                            : selected.engine
                        }
                      />
                      <Field
                        label="Storage"
                        value={selected.storage_gb === 0 ? '512 MB' : `${selected.storage_gb} GB`}
                      />
                      <Field
                        label="Price"
                        value={`${formatCurrency(selected.price_ht)} excl. VAT / ${formatCurrency(
                          selected.price_ttc
                        )} inc. VAT`}
                      />
                    </>
                  )}
                  {selected.type === 'email' && (
                    <>
                      <Field label="Offer" value={selected.offer_name || selected.offer_code} />
                      <Field
                        label="Email account"
                        value={
                          selected.local_part && selected.domain
                            ? `${selected.local_part}@${selected.domain}`
                            : 'Pending configuration'
                        }
                      />
                      <Field
                        label="Price"
                        value={`${formatCurrency(selected.price_ht)} excl. VAT / ${formatCurrency(
                          selected.price_ttc
                        )} inc. VAT`}
                      />
                    </>
                  )}
                  {selected.type === 'messaging' && (
                    <>
                      <Field label="Credits" value={`${selected.credits} message credits`} />
                      <Field label="Price excl. VAT" value={formatCurrency(selected.price_ht)} />
                      <Field label="Price incl. VAT" value={formatCurrency(selected.price_ttc)} />
                      <Field label="Rate" value="€0.02 / credit excl. VAT" />
                    </>
                  )}
                  {selected.type === 'emailing' && (
                    <>
                      <Field label="Credits" value={`${selected.credits} email credits`} />
                      <Field label="Price excl. VAT" value={formatCurrency(selected.price_ht)} />
                      <Field label="Price incl. VAT" value={formatCurrency(selected.price_ttc)} />
                      <Field label="Rate" value="€0.002 / email excl. VAT" />
                    </>
                  )}
                  {selected.type === 'whatsapp' && (
                    <>
                      <Field label="Credits" value={`${selected.credits} WhatsApp credits`} />
                      <Field label="Price excl. VAT" value={formatCurrency(selected.price_ht)} />
                      <Field label="Price incl. VAT" value={formatCurrency(selected.price_ttc)} />
                      <Field label="Rate" value="€0.01 / credit excl. VAT" />
                    </>
                  )}
                  {selected.type === 'vps' && (
                    <>
                      <Field label="Offer" value={selected.offer_name || selected.offer_code} />
                      <Field
                        label="Specifications"
                        value={`${selected.vcores} vCores · ${selected.ram_gb} GB RAM · ${selected.ssd_gb} GB SSD`}
                      />
                      <Field label="Location" value={LOCATION_LABEL[selected.location || ''] || selected.location} />
                      <Field label="Operating System" value={getOSLabel(selected)} />
                      <Field
                        label="Price"
                        value={`${formatCurrency(selected.price_ht)} excl. VAT / ${formatCurrency(
                          selected.price_ttc
                        )} inc. VAT`}
                      />
                    </>
                  )}
                  <Field label="Order date" value={formatDateTime(selected.created_at)} />
                </div>
                {selected.type === 'vps' &&
                  (selected.premium_backup ||
                    selected.snapshot_backup ||
                    (selected.extra_storage_units || 0) > 0) && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="font-medium text-foreground block mb-2 text-sm">Options</span>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1 text-sm">
                        {selected.premium_backup && <li>Automated backups</li>}
                        {selected.snapshot_backup && <li>Snapshot backup</li>}
                        {(selected.extra_storage_units || 0) > 0 && (
                          <li>Extra storage: {(selected.extra_storage_units || 0) * 50} GB</li>
                        )}
                      </ul>
                    </div>
                  )}
              </div>

              {selected.status === 'pending' && <WarningAlert message={pendingMessage(selected.type)} />}
              {selected.status === 'validated' && <SuccessAlert message={validatedMessage(selected)} />}
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)} disabled={deleting}>
                Close
              </Button>
              {selected.status === 'pending' && (
                <Button variant="destructive" onClick={handleCancelOrder} loading={deleting}>
                  {deleting ? 'Cancelling…' : 'Cancel order'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: React.ReactNode; value?: React.ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 text-muted-foreground">{value || '—'}</dd>
    </div>
  );
}
